/**
 * Lookup Field Cell Component
 * 
 * Displays a lookup/masterdetail field value as a clickable link
 * showing the related record's label instead of just the ID.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getGobodoRecordById } from '../../services/gobodo/gobodoRecordService';
import { getRecordById } from '../../services/recordStorageService';
import { getRecordDisplayName } from '../../utils/recordDisplayName';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { useNavigate } from 'react-router-dom';
import { useObjects } from '../../services/queries/objectQueries';

interface LookupFieldCellProps {
  recordId: string;
  lookupObject: string; // The related object API name (e.g., "Case__c")
}

export const LookupFieldCell: React.FC<LookupFieldCellProps> = ({
  recordId,
  lookupObject
}) => {
  const navigate = useNavigate();
  const { data: objects = [] } = useObjects();

  // Find the object ID for the lookup object
  const relatedObject = objects.find(obj => obj.apiName === lookupObject);
  const relatedObjectId = relatedObject?.id || '';

  // Fetch the related record
  const { data: relatedRecord, isLoading, error } = useQuery({
    queryKey: ['lookupRecord', recordId, lookupObject],
    queryFn: async () => {
      if (USE_GOBODO_API) {
        return await getGobodoRecordById(recordId);
      } else {
        return await getRecordById(lookupObject, recordId);
      }
    },
    enabled: !!recordId && !!lookupObject,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Get display name for the related record
  const displayName = relatedRecord 
    ? getRecordDisplayName(lookupObject, relatedRecord)
    : recordId; // Fallback to ID if record not found

  // Handle click to navigate to related record
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    if (relatedObjectId && recordId) {
      // Navigate to admin object detail page with records tab
      // This will show the record list, and user can click the record from there
      // In the future, we could add a direct route to record detail pages
      navigate(`/admin/objects/${relatedObjectId}?tab=records&highlight=${recordId}`);
    }
  };

  if (isLoading) {
    return (
      <span className="text-gray-400 dark:text-gray-500 text-sm">
        Loading...
      </span>
    );
  }

  if (error || !relatedRecord) {
    // If record not found, show ID as fallback
    return (
      <span className="text-gray-400 dark:text-gray-500 text-sm">
        {recordId}
      </span>
    );
  }

  // Render as clickable link
  return (
    <button
      onClick={handleClick}
      className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline text-sm text-left"
      title={`View ${displayName}`}
    >
      {displayName}
    </button>
  );
};
